import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useDeleteVlmPipeline,
  useInvalidateVlmPipelines
} from '@/queries/vlmQueries'
import { showToast } from '@/theme/notifications'
import { Pipeline } from '@/types/vlm'

type VlmPipelineDeleteModalProps = {
  opened: boolean
  pipeline: Pipeline | null
  onClose: () => void
}

export const VlmPipelineDeleteModal = ({
  opened,
  pipeline,
  onClose
}: VlmPipelineDeleteModalProps) => {
  const intl = useIntl()

  const { mutateAsync: deletePipeline, isPending } = useDeleteVlmPipeline()
  const { invalidateVlmPipelineList } = useInvalidateVlmPipelines()

  const handleDelete = async () => {
    if (!pipeline) return

    try {
      await deletePipeline(pipeline.id)
      void invalidateVlmPipelineList()
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'vlm.pipelines.delete.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="vlm.pipelines.delete.title" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Text size="sm">
          <FormattedMessage
            id="vlm.pipelines.delete.confirmation"
            values={{ name: <strong>{pipeline?.name}</strong> }}
          />
        </Text>

        <Group justify="end" mt="xl">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} color="red" onClick={() => void handleDelete()}>
            <FormattedMessage id="vlm.pipelines.delete.confirm" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
